// src/hr/kit/Field.js -- label + control + hint + error.
// type: 'text' (default) | 'date' | 'number' | 'email' | 'search' | 'select' | 'textarea'.
// Or pass one control as the child; it gets the id and aria wiring.
import React, { useId } from 'react';
import { cx, plainId } from './util';

const normalize = (options) => (options || []).map((o) => (
  o !== null && typeof o === 'object' ? o : { value: o, label: String(o) }
));

export function Field({
  label, labelNote, hint, error, type = 'text', options, icon, hideLabel = false,
  id, className, children, ...controlProps
}) {
  const autoId = `hrp-field-${plainId(useId())}`;
  const child = React.Children.count(children) === 1 && React.isValidElement(children) ? children : null;
  const fieldId = id || (child && child.props.id) || autoId;
  const hintId = hint ? `${fieldId}-hint` : null;
  const errorId = error ? `${fieldId}-error` : null;
  const wiring = {
    id: fieldId,
    'aria-invalid': error ? 'true' : undefined,
    'aria-describedby': [hintId, errorId].filter(Boolean).join(' ') || undefined
  };

  let control;
  if (child) {
    control = React.cloneElement(child, wiring);
  } else if (type === 'select') {
    control = (
      <select className="hrp-select" {...wiring} {...controlProps}>
        {normalize(options).map((o) => (
          <option key={String(o.value)} value={o.value} disabled={o.disabled}>{o.label}</option>
        ))}
      </select>
    );
  } else if (type === 'textarea') {
    control = <textarea className="hrp-textarea" rows={3} {...wiring} {...controlProps} />;
  } else {
    control = <input className="hrp-input" type={type} {...wiring} {...controlProps} />;
  }

  return (
    <div className={cx('hrp-field', error && 'hrp-field--error', className)}>
      <label htmlFor={fieldId} className={cx('hrp-field__label', hideLabel && 'hrp-sr')}>
        {label}
        {labelNote && <span className="hrp-field__note">{labelNote}</span>}
      </label>
      <div className={cx('hrp-field__wrap', icon && 'hrp-field__wrap--icon')}>
        {icon && <span className="hrp-field__ic" aria-hidden="true">{icon}</span>}
        {control}
      </div>
      {hint && <div id={hintId} className="hrp-field__hint">{hint}</div>}
      {error && <div id={errorId} className="hrp-field__err" role="alert">{error}</div>}
    </div>
  );
}

export default Field;
